import React, { useState, useEffect } from 'react';
import { heroService, HeroImage } from '../../services/heroService';
import { adminService } from '../../services/adminService';
import ImageUpload from '../ui/ImageUpload';
import ImageCropper from '../ui/ImageCropper';
import LazyImage from '../ui/LazyImage';
import LoadingSpinner from '../ui/LoadingSpinner';
import ConfirmDialog from '../ui/ConfirmDialog';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';

const HeroImageManager: React.FC = () => {
  const [images, setImages] = useState<HeroImage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [croppedPreview, setCroppedPreview] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    active: true,
    imageUrl: ''
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    imageId: string | null;
    imageTitle: string;
  }>({
    isOpen: false,
    imageId: null,
    imageTitle: ''
  });

  useEffect(() => {
    checkRoleAndLoadImages();
  }, []);

  const checkRoleAndLoadImages = async () => {
    try {
      setIsLoading(true);
      const userRole = await adminService.checkCurrentUserRole();

      if (userRole !== 'admin') {
        setError('You do not have permission to manage hero images. Please contact your administrator.');
        setIsLoading(false);
        return;
      }

      await loadImages();
    } catch (err) {
      console.error('Error checking role:', err);
      setError('Error checking permissions');
      setIsLoading(false);
    }
  };

  const loadImages = async () => {
    try {
      const data = await heroService.getAllHeroImages();
      setImages(data.sort((a, b) => a.order - b.order));
      setError(null);
    } catch (err) {
      setError('Error loading hero images');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageSelect = (file: File) => {
    setSelectedImage(file);
    setCroppedPreview(null);
    setShowCropper(true);
    setFormData(prev => ({
      ...prev,
      imageUrl: ''
    }));
  };

  const handleCropComplete = (croppedImage: File) => {
    setSelectedImage(croppedImage);
    // Create a preview URL for the cropped image
    const previewUrl = URL.createObjectURL(croppedImage);
    setCroppedPreview(previewUrl);
    setShowCropper(false);
  };

  const handleCropCancel = () => {
    setSelectedImage(null);
    setCroppedPreview(null);
    setShowCropper(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedImage && !editingId && !formData.imageUrl) {
      setError('Please select an image');
      return;
    }

    try {
      setIsSubmitting(true);
      if (editingId) {
        if (selectedImage) {
          const { imageUrl, ...updatedFormData } = formData;
          await heroService.updateHeroImageWithFile(editingId, selectedImage, updatedFormData);
        } else {
          await heroService.updateHeroImage(editingId, formData);
        }
      } else {
        // For new images, automatically set the order to be last
        const newOrder = images.length > 0 
          ? Math.max(...images.map(img => img.order)) + 1 
          : 1;
        
        await heroService.addHeroImage(
          selectedImage!,
          formData.title,
          formData.description,
          newOrder,
          formData.active
        );
      }
      await loadImages();
      resetForm();
    } catch (err) {
      setError('Error saving hero image');
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (image: HeroImage) => {
    setEditingId(image.id);
    setFormData({
      title: image.title,
      description: image.description,
      active: image.active,
      imageUrl: image.imageUrl
    });
    setCroppedPreview(image.imageUrl);
  };

  const handleDelete = async (id: string) => {
    const imageToDelete = images.find(img => img.id === id);
    if (!imageToDelete) return;

    setDeleteConfirmation({
      isOpen: true,
      imageId: id,
      imageTitle: imageToDelete.title
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmation.imageId) return;

    try {
      setIsSubmitting(true);
      const image = images.find(img => img.id === deleteConfirmation.imageId);
      if (image) {
        await heroService.deleteHeroImage(deleteConfirmation.imageId, image.imageUrl);
        await loadImages();
      }
    } catch (err) {
      setError('Error deleting hero image');
      console.error(err);
    } finally {
      setIsSubmitting(false);
      setDeleteConfirmation({
        isOpen: false,
        imageId: null,
        imageTitle: ''
      });
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmation({
      isOpen: false,
      imageId: null,
      imageTitle: ''
    });
  };

  const resetForm = () => {
    setSelectedImage(null);
    setCroppedPreview(null);
    setFormData({
      title: '',
      description: '',
      active: true,
      imageUrl: ''
    });
    setEditingId(null);
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    // Don't do anything if the item is dropped in the same place
    if (sourceIndex === destinationIndex) return;

    try {
      setIsSubmitting(true);

      // Create a new array with the updated order
      const updatedImages = Array.from(images);
      const [movedItem] = updatedImages.splice(sourceIndex, 1);
      updatedImages.splice(destinationIndex, 0, movedItem);

      // Update the order numbers
      const reorderedImages = updatedImages.map((image, index) => ({
        ...image,
        order: index + 1
      }));

      // Optimistically update the UI
      setImages(reorderedImages);

      // Update all affected images in the database
      await Promise.all(
        reorderedImages.map(image =>
          heroService.updateHeroImage(image.id, { order: image.order })
        )
      );

      setError(null);
    } catch (err) {
      console.error('Error updating image order:', err);
      setError('Failed to update image order');
      // Reload the original order if the update fails
      await loadImages();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Hero Image Management</h2>
          <p className="mt-1 sm:mt-2 text-sm text-gray-600">
            Manage and organize your hero section images. Drag and drop to reorder.
          </p>
        </div>
        <div className="flex items-center space-x-4 text-sm">
          <span className="text-gray-500">
            {images.length} {images.length === 1 ? 'image' : 'images'}
          </span>
        </div>
      </div>
      
      {error && (
        <div className="mb-4 sm:mb-6 bg-red-50 border-l-4 border-red-400 p-3 sm:p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-8">
        <div className="lg:col-span-1 order-2 lg:order-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 divide-y divide-gray-200">
            <div className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingId ? 'Edit Hero Image' : 'Add New Hero Image'}
                </h3>
                {editingId && (
                  <button
                    type="button"
                    onClick={resetForm}
                    className="text-sm text-gray-500 hover:text-gray-700 p-2 -m-2"
                  >
                    Cancel editing
                  </button>
                )}
              </div>
              <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
                <div className="space-y-6 sm:space-y-8">
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-medium text-gray-900">Image</label>
                      <span className="text-xs text-gray-500">Required • 16:9 ratio</span>
                    </div>
                    <div className="space-y-4">
                      <ImageUpload
                        onImageSelect={handleImageSelect}
                        currentImageUrl={formData.imageUrl}
                        disabled={false}
                      />
                      {(croppedPreview || formData.imageUrl) && (
                        <div className="relative rounded-lg overflow-hidden border border-gray-200">
                          <img
                            src={croppedPreview || formData.imageUrl}
                            alt="Image preview"
                            className="w-full h-48 object-cover"
                          />
                          <div className="absolute top-2 right-2">
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedImage(null);
                                setCroppedPreview(null);
                                setFormData(prev => ({
                                  ...prev,
                                  imageUrl: ''
                                }));
                              }}
                              className="p-1.5 bg-white rounded-full shadow-sm hover:bg-gray-50 transition-colors duration-200"
                            >
                              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                          {editingId ? (
                            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs py-1.5 px-3">
                              Current Image • Click "Upload" to change
                            </div>
                          ) : (
                            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs py-1.5 px-3">
                              Preview
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label htmlFor="title" className="block text-sm font-medium text-gray-900">Title</label>
                      <span className="text-xs text-gray-500">Required</span>
                    </div>
                    <input
                      id="title"
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className="block w-full px-4 py-2.5 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      required
                      placeholder="Enter a descriptive title"
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label htmlFor="description" className="block text-sm font-medium text-gray-900">Description</label>
                      <span className="text-xs text-gray-500">Required</span>
                    </div>
                    <textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      className="block w-full px-4 py-2.5 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      rows={3}
                      required
                      placeholder="Enter a brief description"
                    />
                    <p className="mt-2 text-xs text-gray-500">
                      Brief description of the image that will be displayed on the website.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-900 mb-3">Status</label>
                      <div className="relative inline-block w-full">
                        <div
                          className={`flex items-center justify-between w-full px-4 py-3 rounded-md border ${
                            formData.active
                              ? 'bg-green-50 border-green-200'
                              : 'bg-red-50 border-red-200'
                          }`}
                        >
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              name="active"
                              id="active"
                              checked={formData.active}
                              onChange={handleInputChange}
                              className="h-5 w-5 sm:h-4 sm:w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                            />
                            <label htmlFor="active" className="ml-3 block text-sm">
                              {formData.active ? 'Active' : 'Inactive'}
                            </label>
                          </div>
                          <span className={`flex-shrink-0 text-xs ${
                            formData.active ? 'text-green-700' : 'text-red-700'
                          }`}>
                            {formData.active ? '• Visible' : '• Hidden'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    className="w-full flex justify-center items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center">
                        <LoadingSpinner size="sm" className="mr-3" />
                        {editingId ? 'Saving changes...' : 'Adding image...'}
                      </span>
                    ) : (
                      <>
                        {editingId ? (
                          <>
                            <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Save Changes
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Add New Image
                          </>
                        )}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 order-1 lg:order-2">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-0 mb-4">
                <h3 className="text-lg font-medium text-gray-900">Current Hero Images</h3>
                <div className="flex items-center space-x-2 text-sm">
                  <span className="text-gray-500">
                    {images.length} {images.length === 1 ? 'image' : 'images'}
                  </span>
                  <div className="h-4 w-px bg-gray-200 hidden sm:block" />
                  <span className="text-gray-500 hidden sm:block">Drag to reorder</span>
                </div>
              </div>

              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="hero-images">
                  {(provided, snapshot) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className={`grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 ${
                        snapshot.isDraggingOver ? 'bg-gray-50' : ''
                      }`}
                    >
                      {images.map((image, index) => (
                        <Draggable key={image.id} draggableId={image.id} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`bg-white rounded-lg border ${
                                snapshot.isDragging ? 'border-indigo-500 shadow-lg ring-2 ring-indigo-500 ring-opacity-50' : 'border-gray-200'
                              } overflow-hidden transition-all duration-200`}
                            >
                              <div className="relative group touch-manipulation">
                                <div
                                  {...provided.dragHandleProps}
                                  className="absolute top-2 left-2 p-3 sm:p-2 rounded-lg bg-white bg-opacity-90 shadow-sm cursor-move z-10 hover:bg-gray-50 group/handle touch-manipulation"
                                  title="Drag to reorder"
                                >
                                  <svg className="w-6 h-6 sm:w-5 sm:h-5 text-gray-400 group-hover/handle:text-gray-600" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M7 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 2M13 2a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 2M7 8a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 8M13 8a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 8M7 14a2 2 0 1 0 .001 4.001A2 2 0 0 0 7 14M13 14a2 2 0 1 0 .001 4.001A2 2 0 0 0 13 14" />
                                  </svg>
                                </div>
                                <LazyImage
                                  src={image.imageUrl}
                                  alt={image.title}
                                  className="w-full h-48"
                                  aspectRatio={16 / 9}
                                />
                                <div className="absolute top-2 right-2 flex space-x-2">
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    image.active 
                                      ? 'bg-green-100 text-green-800' 
                                      : 'bg-red-100 text-red-800'
                                  }`}>
                                    {image.active ? 'Active' : 'Inactive'}
                                  </span>
                                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                    #{index + 1}
                                  </span>
                                </div>
                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-opacity duration-200">
                                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                    <div className="flex space-x-2">
                                      <button
                                        onClick={() => handleEdit(image)}
                                        className="p-2 bg-white rounded-full shadow-sm hover:bg-gray-50 transition-colors duration-200"
                                        disabled={isSubmitting}
                                        title="Edit image"
                                      >
                                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                      </button>
                                      <button
                                        onClick={() => handleDelete(image.id)}
                                        className="p-2 bg-white rounded-full shadow-sm hover:bg-gray-50 transition-colors duration-200"
                                        disabled={isSubmitting}
                                        title="Delete image"
                                      >
                                        <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="p-4">
                                <h4 className="font-medium text-gray-900">{image.title}</h4>
                                <p className="mt-1 text-sm text-gray-500 line-clamp-2">{image.description}</p>
                                <div className="mt-4 sm:mt-2 flex items-center justify-end">
                                  <div className="flex items-center space-x-2">
                                    <button
                                      onClick={() => handleEdit(image)}
                                      className="p-2 -m-2 text-gray-600 hover:text-indigo-600 transition-colors duration-200"
                                      disabled={isSubmitting}
                                      title="Edit image"
                                    >
                                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                      </svg>
                                    </button>
                                    <button
                                      onClick={() => handleDelete(image.id)}
                                      className="p-2 -m-2 text-gray-600 hover:text-red-600 transition-colors duration-200"
                                      disabled={isSubmitting}
                                      title="Delete image"
                                    >
                                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
          </div>
        </div>
      </div>

      {showCropper && selectedImage && (
        <ImageCropper
          image={selectedImage}
          aspectRatio={16 / 9}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}

      <ConfirmDialog
        isOpen={deleteConfirmation.isOpen}
        title="Delete Hero Image"
        message={`Are you sure you want to delete "${deleteConfirmation.imageTitle}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isDestructive={true}
      />
    </div>
  );
};

export default HeroImageManager; 